// Chess clock as pure functions over a serialisable state, so the local
// scene and (PR-B) the server-authoritative session share one implementation.
// Time is whatever monotonic-enough millisecond clock the caller passes in.
import { lacksMatingMaterial } from './material.js';
import { opposite, type Color } from './types.js';
import type { Outcome } from './outcome.js';

export type ClockPreset = 'bullet' | 'blitz' | 'rapid';

export const CLOCK_PRESETS: readonly ClockPreset[] = ['bullet', 'blitz', 'rapid'];

const MINUTE_MS = 60_000;
export const PRESET_MS: Readonly<Record<ClockPreset, number>> = {
  bullet: 1 * MINUTE_MS,
  blitz: 5 * MINUTE_MS,
  rapid: 10 * MINUTE_MS,
};

/**
 * Clocks start once both sides have made their first move (lichess
 * convention), so nobody loses time to a page still loading.
 */
export const CLOCK_START_PLIES = 2;

export interface ClockState {
  readonly remainingMs: Readonly<Record<Color, number>>;
  /** Side whose clock would run. */
  readonly turn: Color;
  /** When the running side's clock last started; null while stopped. */
  readonly runningSince: number | null;
  readonly plies: number;
}

export function createClock(totalMs: number, turn: Color = 'w'): ClockState {
  return { remainingMs: { w: totalMs, b: totalMs }, turn, runningSince: null, plies: 0 };
}

export function remainingAt(state: ClockState, color: Color, now: number): number {
  const base = state.remainingMs[color];
  if (color !== state.turn || state.runningSince === null) return base;
  return Math.max(0, base - (now - state.runningSince));
}

function settle(state: ClockState, now: number): Readonly<Record<Color, number>> {
  return { ...state.remainingMs, [state.turn]: remainingAt(state, state.turn, now) };
}

/** The side to move just moved: charge its time and start the other clock. */
export function afterMove(state: ClockState, now: number): ClockState {
  const plies = state.plies + 1;
  return {
    remainingMs: settle(state, now),
    turn: opposite(state.turn),
    runningSince: plies >= CLOCK_START_PLIES ? now : null,
    plies,
  };
}

export function pauseClock(state: ClockState, now: number): ClockState {
  if (state.runningSince === null) return state;
  return { ...state, remainingMs: settle(state, now), runningSince: null };
}

export function resumeClock(state: ClockState, now: number): ClockState {
  if (state.runningSince !== null || state.plies < CLOCK_START_PLIES) return state;
  return { ...state, runningSince: now };
}

/** The side that has run out of time, if any. */
export function flaggedSide(state: ClockState, now: number): Color | null {
  return state.runningSince !== null && remainingAt(state, state.turn, now) <= 0 ? state.turn : null;
}

/** Milliseconds until the running side flags; null while stopped. Arms the server's timer. */
export function msUntilFlag(state: ClockState, now: number): number | null {
  return state.runningSince === null ? null : remainingAt(state, state.turn, now);
}

/** FIDE: running out of time loses, unless the opponent could never mate — then it is a draw. */
export function flagOutcome(board: Int8Array, flagged: Color): Outcome {
  const opponent = opposite(flagged);
  return lacksMatingMaterial(board, opponent)
    ? { winner: null, termination: 'flag' }
    : { winner: opponent, termination: 'flag' };
}

/** "4:59", or "0:07.3" under ten seconds. */
export function formatClock(ms: number): string {
  const TENTHS_BELOW_MS = 10_000;
  const clamped = Math.max(0, ms);
  if (clamped < TENTHS_BELOW_MS) return `0:0${(Math.floor(clamped / 100) / 10).toFixed(1)}`;
  const totalSeconds = Math.ceil(clamped / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}
